import * as amplitude from '@amplitude/analytics-browser';

import { SnowplowService } from 'wanderu-service-analytics';

import {
    ERROR,
    CANCEL,
    SUCCESS_PRICE_UP,
    SUCCESS_UNAVAILABLE,
} from '../../../constants';

interface Trip {
    cityName?: string;
    carrierCode?: string;
    departDateTimeUTC?: number;
    localizedDepartDateTime?: string;
    localizedDateTime?: string;
    region?: string;
    tripId?: string;
    wcityid?: string;
}

interface LiveCheckData {
    error?: string[];
    nativeBookingEnabled?: string[];
    notFound?: string[];
    outboundTripIds?: string[];
    prices?: {
        current?: Record<string, number>;
        previous?: Record<string, number>;
    };
    priceDown?: string[];
    priceUp?: string[];
    showLiveCheckMessage?: boolean;
    total?: number;
    unavailable?: string[];
}

const getTimeToDeparture = (departDateTimeUTC: number) => {
    const now = Date.now();
    const date = departDateTimeUTC * 1000; // for some reason this is in seconds

    const diff = Math.abs(date - now);
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    const hours = Math.floor(diff / (60 * 60 * 1000));

    return {
        days,
        hours,
    };
};

const continueClicked = ({ tripId }: { tripId: string }) => {
    const property = { trip_id: tripId };

    SnowplowService.fireStructuredEvent({
        action: 'click',
        category: 'react_search',
        label: 'livecheck_continue_clicked',
        property,
    });

    amplitude.track('livecheck_continue clicked', property);
};

const labelMap = {
    [CANCEL]: 'cancel_livecheck',
    [ERROR]: 'cancel_trip_selection_error',
    [SUCCESS_PRICE_UP]: 'cancel_trip_selection_price_up',
    [SUCCESS_UNAVAILABLE]: 'cancel_trip_selection_not_found',
};

const goBackClicked = ({ tripId, type }: { tripId: string; type: string }) => {
    const property = { trip_id: tripId };

    SnowplowService.fireStructuredEvent({
        action: 'click',
        category: 'react_search',
        label: labelMap[type],
        property,
    });

    amplitude.track(`${labelMap[type]} clicked`, property);
};

const liveCheckInProgressViewed = ({
    carrierCode,
    departDateTimeUTC,
    funnelStage,
    itineraryId,
    // originalPrice, // commenting out for now because it's wrong in some places
    tripId,
    uiViewed,
}: {
    carrierCode?: string;
    departDateTimeUTC?: number;
    funnelStage: 'trip_summary' | 'search';
    itineraryId?: string;
    // originalPrice: string;
    tripId: string;
    uiViewed: 'modal' | 'banner' | 'price-loading-animation';
}) => {
    let days;
    let hours;

    if (departDateTimeUTC) {
        const time = getTimeToDeparture(departDateTimeUTC);
        days = time.days;
        hours = time.hours;
    }

    amplitude.track('livecheck_in_progress_viewed', {
        carrier_code: carrierCode,
        // current_price: originalPrice,
        days_to_departure: days,
        funnel_stage: funnelStage,
        hours_to_departure: hours,
        itinerary_id: itineraryId,
        trip_id: tripId,
        ui_viewed: uiViewed,
    });
};

type ModalState =
    typeof ERROR
    | typeof CANCEL
    | typeof SUCCESS_PRICE_UP
    | typeof SUCCESS_UNAVAILABLE;

const liveCheckResultViewed = ({
    funnelStage,
    itineraryId,
    liveCheckData,
    modalState,
    seats,
    trips,
    uiViewed,
}: {
    funnelStage: 'trip_summary' | 'search';
    itineraryId?: string;
    liveCheckData: LiveCheckData;
    modalState?: ModalState;
    seats?: number;
    trips?: Trip[];
    uiViewed: 'modal' | 'banner';
}) => {
    const {
        error = [],
        notFound = [],
        prices = {},
        priceDown = [],
        priceUp = [],
        unavailable = [],
    } = liveCheckData;

    const liveCheckResults = trips?.map(trip => {
        const {
            carrierCode,
            departDateTimeUTC,
            tripId,
        } = trip;

        let hours;
        let days;

        if (departDateTimeUTC) {
            const time = getTimeToDeparture(departDateTimeUTC);
            days = time.days;
            hours = time.hours;
        }

        let currentPrice;
        let previousPrice;

        if (tripId) {
            currentPrice = prices?.current?.[tripId];
            previousPrice = prices?.previous?.[tripId];
        }
        // try adding livecheck status to each
        return {
            carrier_code: carrierCode,
            current_price: currentPrice,
            days_to_departure: days,
            hours_to_departure: hours,
            previous_price: previousPrice,
            trip_id: tripId,
        };
    });

    let changeType = modalState;

    if (priceDown.length) {
        changeType = 'price_down';
    } else if (priceUp.length) {
        changeType = 'price_up';
    } else if (notFound.length) {
        changeType = 'not_found';
    } else if (error.length) {
        changeType = 'error';
    } else if (unavailable.length) {
        changeType = 'unavailable';
    }

    amplitude.track('livecheck_result_viewed', {
        change_type: changeType,
        funnel_stage: funnelStage,
        itinerary_id: itineraryId,
        livecheck_results: liveCheckResults,
        total_seats_requested: seats,
        ui_viewed: uiViewed,
    });
};

export default {
    continueClicked,
    goBackClicked,
    liveCheckInProgressViewed,
    liveCheckResultViewed,
};
